// The app shell: hash routes (#/ library, #/read/<id>, #/settings[/text|/speech]), sign-in, theme, keyboard.
import { account } from "./api/account";
import { ai } from "./levels/aiService";
import { library } from "./model/library";
import { player } from "./narration/player";
import { Reader } from "./reader/reader";
import { settings, settingsEvents } from "./settings";
import { sync } from "./sync/sync";
import { tagArticle } from "./tags/tagger";
import { openAsk } from "./ui/askSheet";
import { h } from "./ui/dom";
import { LibraryView } from "./ui/library";
import { MiniPlayer } from "./ui/miniPlayer";
import { SettingsView, signInBlock } from "./ui/settings";

type View = { destroy(): void };

export class App {
  private view: View | null = null;
  private reader: Reader | null = null;
  private mini = new MiniPlayer((id) => (location.hash = `#/read/${encodeURIComponent(id)}`));

  constructor(private root: HTMLElement) {}

  async start() {
    document.body.appendChild(this.mini.root);
    this.applyTheme();
    settingsEvents.on("changed", (k) => { if (k === "appearance" || k === "textSize") this.applyTheme(); });
    matchMedia("(prefers-color-scheme: dark)").addEventListener?.("change", () => this.applyTheme());
    window.addEventListener("hashchange", () => this.route());
    document.addEventListener("keydown", (e) => this.key(e));
    window.addEventListener("pagehide", () => { player.save(); this.reader?.savePosition(); });
    account.events.on("changed", () => {
      if (!account.signedIn && this.reader) { location.hash = "#/"; }
      sync.syncNow();
      this.route();
    });
    ai.ready = (k) => sync.levelsReady(k); // DEPTH.md §8: an item's synced rows before generating for it
    await library.loaded;
    await account.refresh();
    sync.start();
    // Tag what this browser added and nobody tagged yet (TAGS.md §3: only one device needs to).
    setTimeout(() => { for (const a of library.list()) if (a.status === "ready" && !a.synced) tagArticle(a.id).catch(() => {}); }, 4000);
    (window as any).depthDebug = {
      reader: () => this.reader?.debugState() ?? null,
      player: () => ({ article: player.articleId, part: player.part, state: player.state.kind, time: player.time }),
      sync: () => ({ syncing: sync.syncing, error: sync.lastError, last: sync.lastSyncAt }),
      ai,
    };
  }

  private applyTheme() {
    const d = document.documentElement;
    const dark = settings.appearance === "dark" || (settings.appearance === "system" && matchMedia("(prefers-color-scheme: dark)").matches);
    d.dataset.theme = dark ? "dark" : "light";
    d.dataset.textSize = String(settings.textSize);
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", dark ? "#111614" : "#f5f2e9");
    this.reader?.applyTextSize();
  }

  private key(e: KeyboardEvent) {
    const t = e.target as HTMLElement;
    if (t.closest("input, textarea, select, [contenteditable]") || document.querySelector(".sheet-backdrop")) return;
    if (this.reader?.onKey(e)) { e.preventDefault(); return; }
    if (!this.reader && e.key === "/" ) { (document.querySelector(".add-link input") as HTMLInputElement | null)?.focus(); e.preventDefault(); }
  }

  private clear() {
    this.view?.destroy();
    this.view = null;
    if (this.reader) { this.reader.destroy(); this.reader = null; }
    this.root.textContent = "";
  }

  route() {
    this.routeView();
    this.mini.setAllowed(account.signedIn && !this.reader);
  }

  private routeView() {
    const hash = location.hash.replace(/^#\/?/, "");
    const [page, arg] = hash.split("/");
    if (!account.signedIn) {
      if (this.view instanceof Welcome) return;
      this.clear();
      const w = new Welcome();
      w.mount(this.root);
      this.view = w;
      return;
    }
    if (page === "read" && arg) {
      if (this.reader && (this.reader as any).article?.id === arg) return;
      const a = library.get(decodeURIComponent(arg));
      if (!a || a.status !== "ready") { location.hash = "#/"; return; }
      this.clear();
      const r = new Reader(a, {
        close: () => { location.hash = "#/"; },
        openSettings: () => (location.hash = "#/settings"),
        openAsk: (art, p) => openAsk(art, p, (src) => {
          const rd = this.reader;
          if (!rd) return;
          if (player.articleId === art.id && player.isLoaded) player.seek(art, src, 0, Number.MAX_SAFE_INTEGER);
          else rd.showChapter(0, src, 0);
        }),
      });
      this.reader = r;
      r.mount(this.root);
      return;
    }
    if (page === "settings") {
      this.clear();
      const v = new SettingsView({ back: () => (location.hash = "#/"), go: (p) => (location.hash = p ? `#/settings/${p}` : "#/settings") },
        arg === "text" || arg === "speech" ? arg : null);
      v.mount(this.root);
      this.view = v;
      return;
    }
    if (this.view instanceof LibraryView) return;
    this.clear();
    const lib = new LibraryView({ open: (id) => (location.hash = `#/read/${encodeURIComponent(id)}`), settings: () => (location.hash = "#/settings") });
    lib.mount(this.root);
    this.view = lib;
  }
}

class Welcome {
  readonly root = h("div.welcome");
  mount(parent: HTMLElement) {
    this.root.append(
      h("div.brand.big", null, h("img", { src: "/icon.png", alt: "" }), "Depth"),
      h("p.eyebrow-text", null, "READ AND LISTEN AT ANY DEPTH"),
      h("h1", null, "Pinch to read closer, or step back for the outline."),
      h("p.muted", null, "Sign in with the account you use on your phone: your library, reading levels and listening progress come with you."),
      account.reachable ? signInBlock() : h("p.error", null, "Can't reach the Depth server right now."));
    parent.appendChild(this.root);
  }
  destroy() { this.root.remove(); }
}
